<template>
  <section class="top-overview">
    <header class="overview-heading">
      <div><h1>TOP Clinics</h1><p>Placement periods and reserved slots by direction.</p></div>
      <div class="heading-actions">
        <button type="button" class="overview-button" :disabled="writing" @click="refresh">Refresh</button>
        <button type="button" class="overview-button primary" :disabled="writing" @click="openEditor()">New period</button>
      </div>
    </header>

    <div class="overview-filters summary-date">
      <label>Slots on date <input v-model="on" aria-label="Slots on date" type="date" :disabled="writing" @change="loadReservations"></label>
      <p class="overview-note">Dates use Kyiv time. Confirmed periods reserve slots; paused periods do not.</p>
    </div>
    <div v-if="writing || reservations.loading || catalog.loading" class="overview-message" role="status">Updating slot summary…</div>
    <div v-else-if="reservations.error" class="overview-message error" role="alert">
      {{ reservations.error }} <button type="button" @click="loadReservations">Retry</button>
    </div>
    <template v-else-if="stats">
      <div class="summary-grid" aria-label="Slot summary">
        <div class="summary-card"><span>Clinics with reserved slots</span><strong>{{ stats.clinics }}</strong></div>
        <div class="summary-card"><span>Homepage reserved</span><strong>{{ stats.homepage }} <small>/ {{ reservations.view.slotLimit }}</small></strong></div>
        <div class="summary-card"><span>Free direction slots</span><strong>{{ stats.free }}</strong><small>{{ stats.full }} of {{ stats.directions }} directions full</small></div>
        <div class="summary-card"><span>Current periods ending within 7 days</span><strong>{{ stats.ending.length }}</strong></div>
      </div>
      <details v-if="stats.ending.length" class="ending-periods">
        <summary>Review {{ stats.ending.length }} ending periods</summary>
        <div v-for="row in stats.ending" :key="row.placement_id" class="ending-row">
          <span>{{ clinicName(row, reservations.view) }} <small>· {{ dates(row).to }}</small></span>
          <button type="button" class="overview-button" @click="openEditor(row, reservations.view)">Manage</button>
        </div>
      </details>
      <top-placement-financials :view="reservations.view" :catalog="catalog.view" :on="on" @view-direction="viewDirection"/>
    </template>

    <div class="overview-tabs" role="tablist" aria-label="TOP Clinics views">
      <button v-for="item in tabs" :id="'tab-' + item.id" :key="item.id" type="button" role="tab"
              :aria-selected="tab === item.id" :aria-controls="'panel-' + item.id"
              :class="{ selected: tab === item.id }" @click="selectTab(item.id)">{{ item.label }}</button>
    </div>

    <div v-if="catalog.error" class="overview-message error" role="alert">
      Directions could not be loaded. <button type="button" @click="loadCatalog">Retry</button>
    </div>

    <section v-if="tab === 'directions'" id="panel-directions" role="tabpanel" aria-labelledby="tab-directions">
      <div class="overview-filters">
        <label class="grow">Find direction <input v-model="directionSearch" type="search" placeholder="Direction name" aria-label="Find direction"></label>
      </div>
      <p class="overview-note">Website display also depends on clinic visibility and page rules.</p>
      <template v-if="reservations.view && catalog.view">
        <p v-if="reservations.view.labelsUnavailable" class="overview-message" role="status">Some clinic names could not be loaded. Reservations are shown by clinic ID.</p>
        <p class="overview-note">{{ directionRows.length }} sections · {{ reservations.view.rows.length }} confirmed periods on this date</p>
        <article v-for="direction in directionRows" :key="direction.id" class="direction-card">
          <div class="direction-heading">
            <h2>{{ direction.name }}</h2>
            <span :class="{ 'over-capacity': direction.rows.length > reservations.view.slotLimit }">
              {{ direction.rows.length }} / {{ reservations.view.slotLimit }} reserved
              <span class="free-count"> · {{ Math.max(0, reservations.view.slotLimit - direction.rows.length) }} free</span>
            </span>
          </div>
          <div class="slot-grid" :style="{ '--slot-count': reservations.view.slotLimit }">
            <router-link v-for="row in direction.rows" :key="row.placement_id" :to="clinicLink(row.clinic_id)" class="slot occupied">
              <el-image v-if="clinicLogo(row, reservations.view)" :src="clinicLogo(row, reservations.view)"
                        class="clinic-thumbnail" fit="contain" alt="">
                <span slot="error" class="clinic-thumbnail-empty" aria-hidden="true">—</span>
              </el-image>
              <span class="slot-clinic">{{ clinicName(row, reservations.view) }}</span>
              <span class="slot-detail">#{{ row.clinic_id }} · Weight {{ row.weight }}</span>
              <span class="slot-detail">{{ dates(row).from }} — {{ dates(row).to }}</span>
              <span v-if="!row.illness_kind_ids.length" class="slot-detail">All directions</span>
            </router-link>
            <div v-for="n in Math.max(0, reservations.view.slotLimit - direction.rows.length)" :key="'free-' + n" class="slot free">Free slot</div>
          </div>
        </article>
        <div v-if="!directionRows.length" class="overview-message">No directions match your search.</div>
      </template>
    </section>

    <section v-else id="panel-placements" role="tabpanel" aria-labelledby="tab-placements">
      <form class="overview-filters" @submit.prevent="applyFilters">
        <div class="overview-field clinic-filter"><span>Clinic</span>
          <el-select v-model="filters.clinic_id" filterable remote clearable :remote-method="searchClinics"
                     :loading="clinicSearch.loading" placeholder="Name or clinic ID" aria-label="Clinic">
            <el-option v-for="clinic in clinicSearch.view || []" :key="clinic.id" :value="clinic.id"
                       :label="clinicLabel(clinic) + ' (#' + clinic.id + ')'"/>
          </el-select>
        </div>
        <div class="overview-field"><span>Direction</span>
          <el-select v-model="filters.illness_kind_id" filterable clearable placeholder="All directions" aria-label="Direction">
            <el-option v-for="direction in catalog.view || []" :key="direction.id" :value="direction.id" :label="direction.name"/>
          </el-select>
        </div>
        <label>Status
          <select v-model="filters.status" aria-label="Status">
            <option value="">All statuses</option><option v-for="status in statuses" :key="status" :value="status">{{ statusLabel(status) }}</option>
          </select>
        </label>
        <label>Overlaps from <input v-model="filters.from" type="date" aria-label="Overlaps from"></label>
        <label>Through <input v-model="filters.to" type="date" aria-label="Through"></label>
        <button type="submit" class="overview-button primary">Apply</button>
        <button type="button" class="overview-button" @click="resetFilters">Reset</button>
      </form>
      <p v-if="clinicSearch.error" class="overview-message error" role="alert">Clinic search failed. Try searching again.</p>
      <p class="overview-note">Direction filters also include periods covering all directions. Manage a period here or open its clinic card.</p>
      <div v-if="registry.loading" class="overview-message" role="status">Loading placement periods…</div>
      <div v-else-if="registry.error" class="overview-message error" role="alert">
        {{ registry.error }} <button type="button" @click="loadRegistry">Retry</button>
      </div>
      <template v-else-if="registry.view">
        <p v-if="registry.view.labelsUnavailable" class="overview-message">Some clinic names could not be loaded. Clinic IDs and periods remain available.</p>
        <div class="placement-list">
          <article v-for="row in registry.view.rows" :key="row.placement_id" class="placement-row">
            <div class="placement-clinic"><span class="field-label">Clinic</span>
              <router-link :to="clinicLink(row.clinic_id)" class="clinic-identity">
                <el-image v-if="clinicLogo(row, registry.view)" :src="clinicLogo(row, registry.view)"
                          class="clinic-thumbnail" fit="contain" alt="">
                  <span slot="error" class="clinic-thumbnail-empty" aria-hidden="true">—</span>
                </el-image>
                <span>{{ clinicName(row, registry.view) }}</span>
              </router-link>
              <small>#{{ row.clinic_id }}</small>
            </div>
            <div><span class="field-label">Directions</span>
              <details v-if="row.illness_kind_ids.length > 2">
                <summary>{{ row.illness_kind_ids.length }} directions</summary>
                <p class="direction-details">{{ directionNames(row) }}</p>
              </details>
              <template v-else>{{ directionNames(row) }}</template>
              <small v-if="row.show_on_homepage">Also on homepage</small>
            </div>
            <div><span class="field-label">Period · Kyiv</span>{{ dates(row).from }}<br>— {{ dates(row).to }}</div>
            <div><span class="field-label">Weight</span><strong>{{ row.weight }}</strong></div>
            <div><span class="field-label">Status</span><span class="status-badge" :class="row.status">{{ statusLabel(row.status) }}</span>
              <small v-if="row.status === 'confirmed'">{{ timing(row) }}</small>
            </div>
            <div class="placement-actions">
              <button type="button" class="overview-button primary" :disabled="writing" @click="openEditor(row, registry.view)">{{ row.status === 'cancelled' ? 'View period' : 'Manage' }}</button>
              <router-link class="open-clinic" :to="clinicLink(row.clinic_id)">Open clinic</router-link>
            </div>
          </article>
        </div>
        <div v-if="!registry.view.rows.length" class="overview-message">No placement periods match these filters.</div>
        <nav class="overview-pagination" aria-label="Placement pages">
          <button type="button" class="overview-button" :disabled="page === 1" @click="changePage(-1)">Previous</button>
          <span>Page {{ page }}</span>
          <button type="button" class="overview-button" :disabled="!registry.view.hasMore" @click="changePage(1)">Next</button>
        </nav>
      </template>
    </section>
    <top-placement-editor v-if="editor" :key="editor.key" :row="editor.row" :clinic-name="editor.clinicName" :catalog="catalog.view || []"
                          @close="editor = null" @mutating="onMutating" @settled="onSettled"/>
  </section>
</template>

<script>
import api from '@/api/modules/clinics';
import catalogs from '@/api/modules/cms-api-catalogs';
import TopPlacementEditor from '@/components/Clinics/TopPlacementEditor';
import TopPlacementFinancials from '@/components/Clinics/TopPlacementFinancials';
import { clinicLink, kyivDay, periodDates, translatedName, overviewView, requestState, loadLatest, invalidate, slotsFor, reservationStats } from '@/components/Clinics/topPlacementOverview';

const emptyFilters = () => ({ clinic_id: '', illness_kind_id: '', status: '', from: '', to: '' });

export default {
  components: { TopPlacementEditor, TopPlacementFinancials },
  data: () => ({
    tab: 'directions',
    tabs: [{ id: 'directions', label: 'Directions' }, { id: 'placements', label: 'Placements' }],
    on: kyivDay(Date.now()), directionSearch: '', filters: emptyFilters(), appliedFilters: {}, page: 1,
    statuses: ['draft', 'confirmed', 'paused', 'cancelled'],
    reservations: requestState(), registry: requestState(), catalog: requestState(), clinicSearch: requestState(),
    editor: null, editorSequence: 0, writing: false,
  }),
  computed: {
    stats() { return reservationStats(this.reservations.view, this.catalog.view, this.on); },
    directionRows() {
      if (!this.reservations.view || !this.catalog.view) return [];
      const view = this.reservations.view;
      const directions = [{ id: 'homepage', name: 'Homepage' }, ...this.catalog.view];
      // Preserve references to retired/missing catalog directions without inventing names.
      const known = new Set(directions.map(item => item.id));
      view.rows.forEach(row => row.illness_kind_ids.forEach(id => {
        if (!known.has(id)) { directions.push({ id, name: 'Direction #' + id }); known.add(id); }
      }));
      return directions.filter(item => item.name.toLowerCase().includes(this.directionSearch.toLowerCase().trim()))
        .map(item => ({ ...item, rows: slotsFor(view, item.id) }))
        .sort((a, b) => a.id === 'homepage' ? -1 : b.id === 'homepage' ? 1 :
          b.rows.length - a.rows.length || a.name.localeCompare(b.name));
    },
  },
  created() { this.loadCatalog(); this.loadReservations(); },
  beforeDestroy() {
    [this.reservations, this.registry, this.catalog, this.clinicSearch].forEach(state => state.generation++);
  },
  methods: {
    clinicLink, dates: periodDates,
    viewDirection(id) {
      const direction = (this.catalog.view || []).find(item => item.id === id);
      if (!direction) return;
      this.directionSearch = direction.name;
      this.tab = 'directions';
      this.$nextTick(() => document.getElementById('panel-directions')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    },
    openEditor(row = null, view = null) {
      if (this.writing) return;
      this.editor = { key: ++this.editorSequence, row, clinicName: row ? this.clinicName(row, view) : '' };
    },
    onMutating() {
      this.writing = true;
      invalidate(this.reservations); invalidate(this.registry);
    },
    onSettled() {
      this.writing = false;
      return Promise.all([this.loadReservations(), this.loadRegistry()]);
    },
    clinicLabel(clinic) { return translatedName(clinic.name) || 'Clinic #' + clinic.id; },
    clinicName(row, view) { return this.clinicLabel(view.clinics[row.clinic_id] || { id: row.clinic_id }); },
    clinicLogo(row, view) { return view.clinics[row.clinic_id]?.logo || ''; },
    statusLabel(status) { return status.charAt(0).toUpperCase() + status.slice(1); },
    timing(row) {
      const dates = periodDates(row);
      const today = kyivDay(Date.now());
      return dates.from > today ? 'Upcoming' : dates.to < today ? 'Ended' : 'Current period';
    },
    directionNames(row) {
      if (!row.illness_kind_ids.length) return 'All directions';
      return row.illness_kind_ids.map(id => (this.catalog.view || []).find(item => item.id === id)?.name || '#' + id).join(', ');
    },
    loadCatalog() {
      return loadLatest(this.catalog, () => catalogs.getIllnessKinds({ perPage: 1500 }), data => {
        if (!data || data.error || !Array.isArray(data.illness_kinds)) throw new Error('Directions unavailable');
        return data.illness_kinds.map(item => ({ id: Number(item.id), name: translatedName(item.name) || 'Direction #' + item.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
      });
    },
    loadReservations() {
      if (this.writing) return;
      return loadLatest(this.reservations, () => {
        if (!this.on) throw new Error('Choose a date to see reserved slots.');
        return api.getTopPlacementReservations({ on: this.on });
      }, data => overviewView(data, true));
    },
    loadRegistry() {
      if (this.writing) return;
      return loadLatest(this.registry, () => api.getTopPlacementRegistry({ ...this.appliedFilters, limit: 25, offset: (this.page - 1) * 25 }),
        data => overviewView(data));
    },
    searchClinics(query) {
      return loadLatest(this.clinicSearch, () => api.getClinics(/^\d+$/.test(query) ?
        { clinic_ids: [Number(query)], limit: 20 } : { clinic_name: query, limit: 20 }), data => {
        if (!data || data.error || !Array.isArray(data.clinics)) throw new Error('Clinic search unavailable');
        return data.clinics;
      });
    },
    applyFilters() {
      this.page = 1;
      this.appliedFilters = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== '' && value !== null));
      return this.loadRegistry();
    },
    resetFilters() { this.filters = emptyFilters(); return this.applyFilters(); },
    changePage(delta) { this.page += delta; return this.loadRegistry(); },
    selectTab(tab) {
      this.tab = tab;
      if (tab === 'placements') this.loadRegistry();
      else this.loadReservations();
    },
    refresh() {
      if (this.writing) return;
      this.loadCatalog();
      return Promise.all([this.loadReservations(), this.loadRegistry()]);
    },
  },
};
</script>

<style scoped>
.top-overview { color: #34465a; min-width: 0; }
.overview-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
.overview-heading h1 { font-size: 28px; margin: 0 0 6px; }
.overview-heading p, .overview-note { color: #657389; margin: 6px 0 16px; font-size: 14px; }
.heading-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.summary-date { background: transparent !important; padding: 0 !important; }
.summary-date .overview-note { margin: 0 0 8px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.summary-card { display: flex; flex-direction: column; gap: 8px; background: white; border: 1px solid #e0e6ec; border-radius: 8px; padding: 18px; font-size: 13px; }
.summary-card strong { font-size: 28px; line-height: 1.2; color: #245980; }
.summary-card small { color: #657389; font-size: 12px; }
.ending-periods { border: 1px solid #e0e6ec; background: #fff; border-radius: 6px; padding: 14px 18px; margin-bottom: 14px; }
.ending-periods summary { cursor: pointer; color: #266b9e; }
.ending-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 12px; }
.ending-row small { color: #657389; }
.placement-actions { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.placement-actions .open-clinic { font-size: 12px; color: #266b9e; }
.overview-tabs { display: flex; gap: 8px; border-bottom: 1px solid #d9e0e8; margin-bottom: 20px; }
.overview-tabs button { background: none; border: 0; border-bottom: 3px solid transparent; padding: 12px 22px; color: #546478; cursor: pointer; font-size: 16px; }
.overview-tabs .selected { color: #2374b7; border-color: #2374b7; font-weight: 600; }
#panel-directions { scroll-margin-top: 80px; }
.overview-filters { display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; background: #fff; padding: 20px; border-radius: 8px; margin-bottom: 16px; }
.overview-filters label, .overview-field { display: flex; flex-direction: column; gap: 6px; margin: 0; font-size: 13px; color: #546478; min-width: 140px; max-width: 100%; }
.overview-filters input, .overview-filters select { height: 40px; border: 1px solid #bcc8d4; border-radius: 5px; padding: 8px 10px; color: #34465a; background: #fff; min-width: 0; width: 100%; }
.overview-filters .grow { flex: 1; min-width: 220px; }
.clinic-filter { width: 250px; }
.overview-button { display: inline-flex; justify-content: center; align-items: center; padding: 9px 15px; min-height: 40px; border: 1px solid #b4c7d7; border-radius: 5px; background: #fff; color: #266b9e; font-size: 14px; cursor: pointer; text-decoration: none; white-space: nowrap; }
.overview-button.primary { background: #287aba; color: white; border-color: #287aba; }
.overview-button:disabled { opacity: .5; cursor: default; }
.overview-button:focus-visible, .slot:focus-visible, .overview-tabs button:focus-visible { outline: 3px solid #8dbce0; outline-offset: 2px; }
.direction-card { background: white; border: 1px solid #e0e6ec; border-radius: 8px; margin-bottom: 14px; padding: 18px; }
.direction-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; font-size: 13px; }
.direction-heading h2 { margin: 0; font-size: 17px; font-weight: 600; color: #34465a; }
.free-count { color: #6a7a8c; }
.over-capacity { color: #b33c34; }
.slot-grid { display: grid; grid-template-columns: repeat(var(--slot-count), minmax(0, 1fr)); gap: 8px; }
.slot { display: flex; flex-direction: column; justify-content: center; gap: 5px; padding: 12px; min-height: 104px; min-width: 0; border-radius: 6px; font-size: 13px; overflow-wrap: anywhere; }
.slot.occupied { justify-content: flex-start; background: #edf5fc; border: 1px solid #c8dff0; text-decoration: none; color: #245980; }
.slot.occupied:hover { background: #e0eef9; border-color: #79afd4; }
.slot-clinic { font-weight: 600; line-height: 1.35; }
.clinic-thumbnail { width: 72px; height: 40px; flex-shrink: 0; background: #fff; border: 1px solid #e0e6ec; border-radius: 4px; padding: 4px; }
.clinic-thumbnail-empty { display: flex; align-items: center; justify-content: center; height: 100%; color: #9aa6b4; }
.clinic-identity { display: flex; align-items: center; gap: 10px; }
.slot-detail { font-size: 11px; color: #5d7287; line-height: 1.4; }
.slot.free { color: #7c8898; border: 1px dashed #d3dce5; text-align: center; background: #fafbfd; }
.overview-message { padding: 24px; background: white; border-radius: 6px; margin-bottom: 16px; color: #63768a; }
.overview-message.error { color: #b23737; background: #fff5f4; }
.overview-message button { margin-left: 12px; }
.placement-list { border-radius: 8px; overflow: hidden; background: white; }
.placement-row { display: grid; grid-template-columns: minmax(180px, 1.5fr) minmax(160px, 1.3fr) minmax(120px, 1fr) 65px 110px 110px; gap: 20px; align-items: center; border-bottom: 1px solid #e1e7ee; padding: 20px; font-size: 14px; }
.placement-row > div { min-width: 0; overflow-wrap: anywhere; }
.placement-clinic a { color: #266b9e; font-weight: 600; }
.field-label { display: block; color: #758397; text-transform: uppercase; letter-spacing: .035em; font-size: 10px; margin-bottom: 6px; }
.placement-row small { display: block; color: #7a8797; margin-top: 5px; }
.placement-row summary { cursor: pointer; color: #266b9e; }
.direction-details { font-size: 13px; margin: 8px 0 0; line-height: 1.6; }
.status-badge { display: inline-block; padding: 4px 8px; border-radius: 4px; background: #edf0f4; font-size: 12px; }
.status-badge.confirmed { color: #277047; background: #e8f5ec; }
.status-badge.paused { color: #98651d; background: #fff4df; }
.status-badge.cancelled { color: #7c7272; background: #f1eded; }
.overview-pagination { display: flex; justify-content: center; align-items: center; gap: 20px; padding: 22px; }
@media (max-width: 1400px) { .slot-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (max-width: 1100px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .placement-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr); }
  .open-clinic { justify-self: start; }
  .slot-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 600px) {
  .overview-heading { align-items: flex-start; flex-direction: column; }
  .overview-heading h1 { font-size: 23px; }
  .overview-filters { padding: 14px; }
  .overview-filters label, .overview-field, .overview-filters .grow { width: 100%; flex: auto; min-width: 0; }
  .placement-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); padding: 14px; gap: 16px; }
  .slot-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .direction-card { padding: 14px; }
}
</style>
